import React, { useEffect, useState } from 'react';
import { supabase } from '../config/supabase';
import { Profile } from '../types';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

interface TipoNovedad {
  nombre: string;
}

interface NovedadActiva {
  id: string;
  empleado_id: string;
  fecha_hora_inicio: string;
  tipo_novedad: TipoNovedad;
}

interface EmployeeWithNovedad extends Profile {
  active_novedad: NovedadActiva | null;
}

const EmployeeList = () => {
  const [employees, setEmployees] = useState<EmployeeWithNovedad[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user) {
      fetchEmployees();
      setupRealtimeSubscription();
    }
  }, [user]);

  const fetchEmployees = async () => {
    try {
      const { data: profilesData, error } = await supabase
        .from('profiles')
        .select(`
          id,
          name,
          email,
          is_active,
          last_activity,
          created_at,
          updated_at,
          created_by,
          updated_by,
          role
        `)
        .order('name');

      if (error) throw error;

      // Obtener novedades activas en una consulta separada
      const { data: novedadesData, error: novedadesError } = await supabase
        .from('registro_novedad')
        .select('*, tipo_novedad!inner(*)')
        .eq('estado', 'En curso');

      if (novedadesError) {
        console.error('Error fetching novedades:', novedadesError);
      }

      console.log('Novedades activas:', novedadesData);

      // Mapear las novedades a los empleados
      const employeesWithNovedad = profilesData?.map(employee => {
        const activeNovedad = novedadesData?.find(
          novedad => novedad.empleado_id === employee.id
        );
        
        console.log(`Empleado ${employee.name}:`, {
          id: employee.id,
          activeNovedad
        });

        return {
          ...employee,
          active_novedad: activeNovedad ? {
            id: activeNovedad.id,
            empleado_id: activeNovedad.empleado_id,
            fecha_hora_inicio: activeNovedad.fecha_hora_inicio,
            tipo_novedad: activeNovedad.tipo_novedad
          } : null
        } as EmployeeWithNovedad;
      });

      setEmployees(employeesWithNovedad || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    const subscription = supabase
      .channel('profiles-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles'
        },
        () => {
          fetchEmployees();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  };

  const filteredEmployees = employees.filter(employee => {
    const searchLower = searchTerm.toLowerCase();
    return (
      employee.name?.toLowerCase().includes(searchLower) ||
      employee.email?.toLowerCase().includes(searchLower) ||
      employee.role?.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Lista de Empleados
            </h1>
            <div className="w-full sm:w-64">
              <input
                type="text"
                placeholder="Buscar empleados..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-700">
            Total de empleados: {employees.length}
          </p>
        </div>

        {/* Lista de empleados para móvil */}
        <div className="sm:hidden space-y-4">
          {filteredEmployees.map((employee) => (
            <div
              key={employee.id}
              className="bg-white shadow rounded-lg overflow-hidden"
            >
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {employee.name || 'Sin nombre'}
                    </h3>
                    <p className="text-sm text-gray-500">{employee.email}</p>
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        employee.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {employee.is_active ? 'En línea' : 'Desconectado'}
                    </span>
                    {employee.active_novedad && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {employee.active_novedad.tipo_novedad.nombre}
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Rol:</span>
                    <span className="text-sm text-gray-900">{employee.role}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Última actividad:</span>
                    <span className="text-sm text-gray-900">
                      {employee.last_activity ? formatDate(employee.last_activity) : 'N/A'}
                    </span>
                  </div>
                  {employee.active_novedad && (
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-gray-500">Inicio novedad:</span>
                      <span className="text-sm text-gray-900">
                        {new Date(employee.active_novedad.fecha_hora_inicio).toLocaleTimeString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabla para desktop */}
        <div className="hidden sm:block">
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rol
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Última Actividad
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEmployees.map((employee) => (
                    <tr key={employee.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {employee.name || 'Sin nombre'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{employee.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{employee.role}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              employee.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {employee.is_active ? 'En línea' : 'Desconectado'}
                          </span>
                          {employee.active_novedad && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              {employee.active_novedad.tipo_novedad.nombre} - 
                              {new Date(employee.active_novedad.fecha_hora_inicio).toLocaleTimeString()}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {employee.last_activity ? formatDate(employee.last_activity) : 'N/A'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Mensaje cuando no hay resultados */}
        {filteredEmployees.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No se encontraron empleados</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default EmployeeList;
